import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, User, Upload, X, Sparkles, MessageCircle, LogOut, Camera, Star } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [socialScore, setSocialScore] = useState(null);

  useEffect(() => {
    loadProfile();
    loadSocialScore();
  }, []);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/profile/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      toast.error('Erreur de chargement du profil');
    }
  };

  const loadSocialScore = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/ratings/my-scores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSocialScore(response.data);
    } catch (error) {
      console.error('Error loading social score:', error);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/profile/photos`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Photo ajoutée !');
      loadProfile();
    } catch (error) {
      toast.error('Erreur d\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoDelete = async (photoId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/profile/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo supprimée');
      loadProfile();
    } catch (error) {
      toast.error('Erreur de suppression');
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'SUPPRIMER') {
      toast.error('Veuillez taper SUPPRIMER pour confirmer');
      return;
    }

    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/profile/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Compte supprimé avec succès');
      localStorage.clear();
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
      setDeleting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Heart className="w-8 h-8 text-pink-500 fill-pink-500" />
            <h1 className="text-3xl font-bold gradient-text">A-More AI</h1>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-pink-600"
            style={{ transition: 'color 0.2s' }}
          >
            <LogOut className="w-5 h-5" />
            <span>Déconnexion</span>
          </button>
        </div>

        {/* Navigation */}
        <div className="glass-card p-4 mb-8">
          <div className="flex gap-4 flex-wrap">
            <button
              data-testid="nav-dashboard-btn"
              className="px-6 py-3 bg-gradient-to-r from-pink-500 to-orange-400 text-white rounded-full font-medium"
            >
              Mon Profil
            </button>
            <button
              data-testid="nav-discover-btn"
              onClick={() => navigate('/discover')}
              className="px-6 py-3 bg-white text-gray-700 rounded-full font-medium hover:bg-gray-50"
              style={{ transition: 'background 0.2s' }}
            >
              Découvrir
            </button>
            <button
              data-testid="nav-matches-btn"
              onClick={() => navigate('/matches')}
              className="px-6 py-3 bg-white text-gray-700 rounded-full font-medium hover:bg-gray-50"
              style={{ transition: 'background 0.2s' }}
            >
              Mes Matchs
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="glass-card p-6">
              <div className="text-center mb-6">
                <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gradient-to-br from-pink-400 to-orange-400 flex items-center justify-center">
                  <User className="w-16 h-16 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-1">{user.name}</h2>
                <p className="text-gray-600">{user.age} ans • {user.location}</p>
                {user.is_premium && (
                  <div className="inline-flex items-center gap-1 mt-2 px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-full text-sm font-medium">
                    <Sparkles className="w-4 h-4" />
                    Premium
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Bio</h3>
                  <p className="text-gray-600 text-sm">{user.bio || 'Aucune bio pour le moment'}</p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Intérêts</h3>
                  <div className="flex flex-wrap gap-2">
                    {user.interests?.length > 0 ? (
                      user.interests.map(interest => (
                        <span
                          key={interest}
                          className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm"
                        >
                          {interest}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500 text-sm">Aucun intérêt ajouté</span>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Type recherché</h3>
                  <p className="text-gray-600 text-sm">{user.type_preference || 'Non spécifié'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Photos */}
          <div className="lg:col-span-2">
            <div className="glass-card p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-6">Mes Photos</h3>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                {user.photos?.map(photoId => (
                  <div key={photoId} className="relative group aspect-square rounded-lg overflow-hidden">
                    <img
                      src={`${API}/profile/photos/${photoId}`}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                    <button
                      data-testid={`delete-photo-${photoId}`}
                      onClick={() => handlePhotoDelete(photoId)}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100"
                      style={{ transition: 'opacity 0.2s' }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                {/* Upload Button */}
                <label className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-pink-500 cursor-pointer flex flex-col items-center justify-center">
                  <input
                    data-testid="photo-upload-input"
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  {uploading ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500" />
                  ) : (
                    <>
                      <Camera className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">Ajouter</span>
                    </>
                  )}
                </label>
              </div>

              {user.photos?.length === 0 && (
                <div className="text-center py-12">
                  <Upload className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 mb-2">Aucune photo ajoutée</p>
                  <p className="text-sm text-gray-500">Ajoutez des photos pour augmenter vos chances de match !</p>
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-gray-200">
                {/* Social Score Section */}
                {socialScore && socialScore.total_ratings > 0 && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl">
                    <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      Mes Évaluations Reçues
                    </h3>
                    
                    <div className="flex items-center gap-3 mb-4">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-gray-800">{socialScore.average}</div>
                        <div className="text-xs text-gray-600">Score Global</div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map(star => (
                            <Star
                              key={star}
                              className={`w-5 h-5 ${
                                star <= Math.round(socialScore.average)
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          Basé sur {socialScore.total_ratings} avis
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">Confiance</span>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map(star => (
                              <Star
                                key={star}
                                className={`w-3 h-3 ${
                                  star <= Math.round(socialScore.confiance)
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="font-semibold text-gray-800">{socialScore.confiance}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">Sérieux</span>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map(star => (
                              <Star
                                key={star}
                                className={`w-3 h-3 ${
                                  star <= Math.round(socialScore.serieux)
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="font-semibold text-gray-800">{socialScore.serieux}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">Générosité</span>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map(star => (
                              <Star
                                key={star}
                                className={`w-3 h-3 ${
                                  star <= Math.round(socialScore.generosite)
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="font-semibold text-gray-800">{socialScore.generosite}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">Comportement</span>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map(star => (
                              <Star
                                key={star}
                                className={`w-3 h-3 ${
                                  star <= Math.round(socialScore.comportement)
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="font-semibold text-gray-800">{socialScore.comportement}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">Communication</span>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map(star => (
                              <Star
                                key={star}
                                className={`w-3 h-3 ${
                                  star <= Math.round(socialScore.communication)
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="font-semibold text-gray-800">{socialScore.communication}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  data-testid="start-matching-btn"
                  onClick={() => navigate('/discover')}
                  className="w-full btn-primary py-4 flex items-center justify-center gap-2 mb-4"
                >
                  <Sparkles className="w-5 h-5" />
                  Commencer le matching
                </button>

                <button
                  data-testid="delete-account-btn"
                  onClick={() => setShowDeleteModal(true)}
                  className="w-full px-4 py-3 text-red-600 hover:text-red-700 text-sm font-medium"
                  style={{ transition: 'color 0.2s' }}
                >
                  Supprimer mon compte
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Account Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/50">
            <div className="glass-card p-8 max-w-md w-full">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Supprimer votre compte ?</h3>
              
              <div className="space-y-4 mb-6">
                <p className="text-gray-700">
                  Cette action est <strong>irréversible</strong>. Toutes vos données seront définitivement supprimées :
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Votre profil et photos</li>
                  <li>Vos matchs et conversations</li>
                  <li>Votre historique de swipes</li>
                  <li>Toutes vos données personnelles</li>
                </ul>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-4 mt-4">
                  <p className="text-sm text-red-800">
                    ⚠️ Vous ne pourrez pas récupérer votre compte après suppression.
                  </p>
                </div>

                <div className="mt-6">
                  <label className="block text-gray-700 font-medium mb-2">
                    Pour confirmer, tapez <strong>SUPPRIMER</strong> :
                  </label>
                  <input
                    data-testid="delete-confirm-input"
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="SUPPRIMER"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  data-testid="cancel-delete-btn"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmText('');
                  }}
                  disabled={deleting}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                  style={{ transition: 'background 0.2s' }}
                >
                  Annuler
                </button>
                <button
                  data-testid="confirm-delete-btn"
                  onClick={handleDeleteAccount}
                  disabled={deleting || deleteConfirmText !== 'SUPPRIMER'}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ transition: 'all 0.2s' }}
                >
                  {deleting ? 'Suppression...' : 'Supprimer définitivement'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
